import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {ColumnModel, SettingsList} from '../../../../components/Settings/SettingsList';
import {VirtualControlModel} from '../../../../store/control/state';
import {useSetSelector} from '../../../../hooks/set-selector';
import {useSortBySelector} from '../../../../hooks/sort-by-selector';
import {useHistory} from 'react-router-dom';
import {useFilter} from '../../../../hooks/filter';
import {useLocalization} from "../../../../hooks/localization";
import {useRPC} from '../../../../hooks/rpc';

const keyExtractor = (item: VirtualControlModel) => item.id;

export const VirtualControlListScreen = memo(function VirtualControlListScreen() {
    const {t} = useLocalization();
    const history = useHistory();

    const virtualControlList = useFilter(useSortBySelector(state => state.control.virtualControlList, control => control.bridge_device_id), c => c.registered_by_system !== true);
    const idBridgeDeviceMap = useSetSelector(state => state.control.bridgeDeviceList);
    const idControlMap = useSetSelector(state => state.control.controlList);

    const [virtualControlCountValue, setVirtualControlCountValue] = useState<number>(0)
    const [rpc] = useRPC();

    const [selectedVirtualControl, setSelectedVirtualControl] = useState<VirtualControlModel>(null);

    const onCopyClick = useCallback(() => {
        history.push({pathname: '/settings/virtual-control/edit', state: {...selectedVirtualControl, id: null}});
    }, [history, selectedVirtualControl]);

    const buttons = useMemo(() => [
        {
            text: t('copy'),
            onClick: onCopyClick,
            className: 'blue',
            hidden: !(selectedVirtualControl),
        },
        {
            text: t('edit'),
            url: `/settings/virtual-control/edit/${selectedVirtualControl ? selectedVirtualControl.id : ''}`,
            className: 'orange',
            hidden: !(selectedVirtualControl),
        },
        {
            text: t('create_virtual_control'),
            url: '/settings/virtual-control/edit',
            className: 'green',
        },
    ], [t, onCopyClick, selectedVirtualControl]);

    const displayNameExtractor = useCallback((item: VirtualControlModel, column: ColumnModel) => {
        if (column.key === 'bridge_device_name') {
            return idBridgeDeviceMap[item.bridge_device_id]?.name ?? '-';
        } else if (column.key === 'control_name') {
            return idControlMap[item.control_id]?.name ?? '-';
        }

        return item[column.key] ?? '-';
    }, [idBridgeDeviceMap, idControlMap]);

    const columns = useMemo<ColumnModel[]>(() => [

        {
            length: 3,
            name: `${t('control_name')} (${virtualControlCountValue})`,
            key: 'control_name',
        },
        {
            length: 2,
            name: t('slot'),
            key: 'slot',
        },
        {
            length: 3,
            name: t('device_name'),
            key: 'bridge_device_name',
        },
    ], [t, virtualControlCountValue]);

    useEffect(() => {
        rpc.getVirtualControlCount().then(r => setVirtualControlCountValue(r))
    }, [rpc])

    return (
        <PasswordContainer requiredRole="service" isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                backUrl="/settings"
                searchable={true}>
                <SettingsList
                    columns={columns}
                    items={virtualControlList}
                    displayNameExtractor={displayNameExtractor}
                    keyExtractor={keyExtractor}
                    selectedItem={selectedVirtualControl}
                    onItemSelected={setSelectedVirtualControl}/>
            </SettingsPageContainer>
        </PasswordContainer>
    );
});
